import { validatePrintableLicense } from './printableLicense.js';
import { embeddedPngBytes, EMBEDDED_PNG_MAX_BYTES, EMBEDDED_PNG_MAX_DIMENSION } from './embeddedPng.js';

/**
 * Municipality-configured emails triggered by license status transitions.
 * Pure helpers with no external dependencies.
 */

export const LicenseStatusEmailMode = Object.freeze({
  disabled: 'disabled',
  confirmBeforeSending: 'confirm_before_sending',
  sendAutomatically: 'send_automatically'
});

export const LICENSE_STATUS_EMAIL_MODES = Object.freeze(
  Object.values(LicenseStatusEmailMode)
);

export const LicenseStatusEmailTriggerType = Object.freeze({
  statusEntered: 'status_entered'
});

export const LicenseStatusEmailRepeatPolicy = Object.freeze({
  oncePerLicense: 'once_per_license',
  oncePerTerm: 'once_per_term',
  everyEntry: 'every_entry'
});

export const LicenseStatusEmailNotificationDecision = Object.freeze({
  send: 'send',
  skip: 'skip'
});

export const LicenseStatusEmailNotificationOutcome = Object.freeze({
  queued: 'queued',
  disabled: 'disabled',
  skippedByStaff: 'skipped_by_staff',
  missingRecipient: 'missing_recipient',
  alreadyEvaluated: 'already_evaluated',
  alreadyQueued: 'already_queued'
});

export const LEGACY_LICENSE_APPROVAL_EMAIL_RULE_ID = 'license-approval';
export const LICENSE_STATUS_EMAIL_TEMPLATE_TEXT_MAX_LENGTH = 5000;
export const LICENSE_STATUS_EMAIL_SUBJECT_MAX_LENGTH = 300;
export const LICENSE_STATUS_EMAIL_MARKDOWN_MAX_LENGTH = 20000;
export const EMAIL_TEMPLATE_IMAGE_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;
export const EMAIL_TEMPLATE_IMAGE_MAX_COUNT = 8;

// Controls newly inserted images; both formats remain readable in either mode.
export const EMAIL_TEMPLATE_IMAGE_STORAGE_MODE = 'embedded'; // 'embedded' or 'storage'
export const EMAIL_TEMPLATE_EMBEDDED_IMAGE_MAX_BYTES = EMBEDDED_PNG_MAX_BYTES;
export const EMAIL_TEMPLATE_EMBEDDED_TOTAL_MAX_BYTES = 200 * 1024;
export const EMAIL_TEMPLATE_EMBEDDED_SETTINGS_MAX_BYTES = 300 * 1024;
export const EMAIL_TEMPLATE_SETTINGS_MAX_BYTES = 512 * 1024;
export const EMAIL_TEMPLATE_EMBEDDED_IMAGE_MAX_DIMENSION = EMBEDDED_PNG_MAX_DIMENSION;

export const LICENSE_STATUS_EMAIL_FIELDS = Object.freeze([
  { name: 'licenseId', label: 'License number' },
  { name: 'licenseeNames', label: 'Licensee names' },
  { name: 'address', label: 'Street address' },
  { name: 'fullAddress', label: 'Full property address' },
  { name: 'issuedOn', label: 'Issued date' },
  { name: 'expiresOn', label: 'Expiration date' },
  { name: 'statusLabel', label: 'Status' },
  { name: 'previousStatusLabel', label: 'Previous status' },
  { name: 'municipalityName', label: 'Municipality name' },
  { name: 'signature', label: 'Municipal office name' },
  { name: 'contactEmail', label: 'Municipal contact email' }
]);

/** Extracts an immutable image identifier without accepting arbitrary storage paths. */
export function emailTemplateImageId(source) {
  return typeof source === 'string'
    ? source.match(/^email-image:([a-f0-9]{64}\.png)$/)?.[1] || null
    : null;
}

/** Identifies an image held inside the template, independently of Storage paths. */
export function embeddedEmailImageId(source) {
  return typeof source === 'string'
    ? source.match(/^email-embedded:([a-f0-9]{32}\.png)$/)?.[1] || null
    : null;
}

/** Returns the decoded byte count of a bounded PNG data URL, or null for invalid input. */
export function embeddedEmailImageBytes(dataUrl) {
  return embeddedPngBytes(dataUrl);
}

/** Validates embedded image shapes and byte budgets in both the browser and API. */
export function validateEmbeddedEmailImages(images) {
  if (images === undefined) return [];
  if (!images || typeof images !== 'object' || Array.isArray(images)) {
    return ['Embedded email images must be an object.'];
  }

  const entries = Object.entries(images);
  const errors = [];
  let total = 0;

  if (entries.length > EMAIL_TEMPLATE_IMAGE_MAX_COUNT) {
    errors.push(`Use no more than ${EMAIL_TEMPLATE_IMAGE_MAX_COUNT} embedded images per email.`);
  }

  for (const [id, dataUrl] of entries) {
    const bytes = embeddedEmailImageBytes(dataUrl);

    if (!embeddedEmailImageId(`email-embedded:${id}`) || bytes === null) {
      errors.push('Each embedded image must be a PNG no larger than 100 KB.');
      break;
    }

    total += bytes;
  }

  if (total > EMAIL_TEMPLATE_EMBEDDED_TOTAL_MAX_BYTES) {
    errors.push('Embedded images must total 200 KB or less per email.');
  }

  return errors;
}

const RULE_ID_PATTERN = /^[A-Za-z0-9_-]{1,100}$/;
const REPEAT_POLICIES = new Set(Object.values(LicenseStatusEmailRepeatPolicy));
const TRIGGER_TYPES = new Set(Object.values(LicenseStatusEmailTriggerType));

export function normalizeLicenseStatusEmailText(value) {
  if (typeof value !== 'string') return null;
  return value.trim() || null;
}

export function normalizeLicenseStatusEmailRuleId(value) {
  const normalized = normalizeLicenseStatusEmailText(value);
  return normalized && RULE_ID_PATTERN.test(normalized) ? normalized : null;
}

export function isLicenseStatusEmailMode(value) {
  return LICENSE_STATUS_EMAIL_MODES.includes(value);
}

export function isLicenseStatusEmailNotificationDecision(value) {
  return Object.values(LicenseStatusEmailNotificationDecision).includes(value);
}

export function normalizeLicenseStatusEmailTrigger(value) {
  const trigger = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};

  return {
    type: LicenseStatusEmailTriggerType.statusEntered,
    statusCode: normalizeLicenseStatusEmailText(trigger.statusCode)
  };
}

export function normalizeLicenseStatusEmailTemplate(value) {
  const template = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
  const message = normalizeLicenseStatusEmailText(template.message);

  return {
    ...(template.format && template.format !== 'standard' ? { format: template.format } : {}),
    subject: normalizeLicenseStatusEmailText(template.subject),
    // Leading indentation and trailing spaces can carry meaning in Markdown.
    message: template.format === 'markdown' && message ? template.message : message,
    ...(template.images && typeof template.images === 'object' && !Array.isArray(template.images)
      ? { images: { ...template.images } }
      : {})
  };
}

function firstActiveStatusCode(statusConfig = []) {
  return (Array.isArray(statusConfig) ? statusConfig : [])
    .map(status => ({
      ...status,
      code: normalizeLicenseStatusEmailText(status?.code || status?.id)
    }))
    .find(status => status?.type === 'active')?.code || null;
}

export function normalizeLicenseStatusEmailRule(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const id = normalizeLicenseStatusEmailRuleId(value.id);
  if (!id) {
    return null;
  }

  return {
    id,
    name: normalizeLicenseStatusEmailText(value.name) || id,
    trigger: normalizeLicenseStatusEmailTrigger(value.trigger),
    mode: isLicenseStatusEmailMode(value.mode)
      ? value.mode
      : LicenseStatusEmailMode.disabled,
    repeatPolicy: REPEAT_POLICIES.has(value.repeatPolicy)
      ? value.repeatPolicy
      : LicenseStatusEmailRepeatPolicy.oncePerLicense,
    template: normalizeLicenseStatusEmailTemplate(value.template),
    ...(value.attachPrintableLicense !== undefined || value.certificate ? {
      attachPrintableLicense: value.attachPrintableLicense ?? Boolean(value.certificate),
      sendOnRenewal: value.sendOnRenewal ?? (value.certificate?.sendOnRenewal !== false)
    } : {})
  };
}

/**
 * Convert the original singular approval email settings into one status-email
 * rule. This is intentionally deterministic so existing cities all receive
 * the same stable rule ID during the compatibility period.
 */
export function buildLegacyLicenseApprovalEmailRule(value, { statusConfig = [] } = {}) {
  const settings = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
  const triggerStatusCode = normalizeLicenseStatusEmailText(settings.triggerStatusCode)
    || firstActiveStatusCode(statusConfig);
  const customMessage = normalizeLicenseStatusEmailText(settings.customMessage);

  return normalizeLicenseStatusEmailRule({
    id: LEGACY_LICENSE_APPROVAL_EMAIL_RULE_ID,
    name: 'Approval email',
    trigger: triggerStatusCode
      ? {
          type: LicenseStatusEmailTriggerType.statusEntered,
          statusCode: triggerStatusCode
        }
      : {
          type: LicenseStatusEmailTriggerType.statusEntered,
          statusCode: null
        },
    mode: settings.mode,
    repeatPolicy: LicenseStatusEmailRepeatPolicy.oncePerLicense,
    template: {
      subject: 'Your license has been approved',
      message: customMessage
        || 'Your license application{{licenseAddressPhrase}} has been approved. Your license is now active.'
    }
  });
}

/**
 * Normalize the new collection. When it has not been stored yet, an existing
 * singular approval-email object is exposed as one legacy rule.
 */
export function normalizeLicenseStatusEmails(value, { legacyApprovalEmail, statusConfig = [] } = {}) {
  if (value && typeof value === 'object' && !Array.isArray(value) && Array.isArray(value.rules)) {
    return {
      ...value,
      rules: value.rules
        .map(normalizeLicenseStatusEmailRule)
        .filter(Boolean)
    };
  }

  return {
    rules: legacyApprovalEmail && typeof legacyApprovalEmail === 'object'
      ? [buildLegacyLicenseApprovalEmailRule(legacyApprovalEmail, { statusConfig })]
      : []
  };
}

export function isLicenseStatusEmailRuleTransition({
  rule,
  previousStatus,
  nextStatus
} = {}) {
  const normalizedRule = normalizeLicenseStatusEmailRule(rule);
  if (!normalizedRule || !nextStatus || previousStatus === nextStatus) {
    return false;
  }

  const { trigger } = normalizedRule;
  return Boolean(trigger.statusCode) && nextStatus === trigger.statusCode;
}

export function isLicenseStatusEmailRuleStatusEligible({
  rule,
  currentStatus
} = {}) {
  const normalizedRule = normalizeLicenseStatusEmailRule(rule);
  if (!normalizedRule || !currentStatus) {
    return false;
  }

  const { trigger } = normalizedRule;
  return Boolean(trigger.statusCode) && currentStatus === trigger.statusCode;
}

export function matchLicenseStatusEmailRules({
  rules = [],
  previousStatus,
  nextStatus,
  statusConfig = [],
  renewal = false,
  includeDisabled = false
} = {}) {
  return rules
    .map(normalizeLicenseStatusEmailRule)
    .filter(Boolean)
    .filter(rule => includeDisabled || rule.mode !== LicenseStatusEmailMode.disabled)
    .filter(rule => renewal
      ? rule.attachPrintableLicense && rule.sendOnRenewal && rule.trigger.statusCode === nextStatus
        && statusConfig.some(status => (status.code || status.id) === nextStatus && status.type === 'active')
      : isLicenseStatusEmailRuleTransition({
      rule,
      previousStatus,
      nextStatus,
      statusConfig
    }));
}

export function getLicenseStatusEmailRulesRequiringConfirmation(args = {}) {
  return matchLicenseStatusEmailRules(args)
    .filter(rule => rule.mode === LicenseStatusEmailMode.confirmBeforeSending);
}

/**
 * Return validation messages suitable for translating into an API 400.
 */
export function validateLicenseStatusEmails(value, { statusConfig = [], cityId } = {}) {
  const errors = [];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return ['licenseStatusEmails must be an object'];
  }
  if (!Array.isArray(value.rules)) {
    return ['licenseStatusEmails.rules must be an array'];
  }

  // Rules share one municipal document, so per-image limits alone are insufficient.
  if (new TextEncoder().encode(JSON.stringify(value)).length > EMAIL_TEMPLATE_SETTINGS_MAX_BYTES) {
    return ['License email settings must total 512 KB or less, including embedded images.'];
  }

  let embeddedBytes = 0;

  const configuredStatusCodes = new Set(
    (Array.isArray(statusConfig) ? statusConfig : [])
      .map(status => normalizeLicenseStatusEmailText(status?.code || status?.id))
      .filter(Boolean)
  );
  const seenIds = new Set();
  const seenStatusTriggers = new Set();

  value.rules.forEach((rawRule, index) => {
    const path = `licenseStatusEmails.rules[${index}]`;
    if (!rawRule || typeof rawRule !== 'object' || Array.isArray(rawRule)) {
      errors.push(`${path} must be an object`);
      return;
    }

    const id = normalizeLicenseStatusEmailRuleId(rawRule.id);
    if (!id) {
      errors.push(`${path}.id must contain only letters, numbers, hyphens, or underscores`);
    } else if (seenIds.has(id)) {
      errors.push(`${path}.id must be unique`);
    } else {
      seenIds.add(id);
    }

    if (!normalizeLicenseStatusEmailText(rawRule.name)) {
      errors.push(`${path}.name is required`);
    }
    if (!isLicenseStatusEmailMode(rawRule.mode)) {
      errors.push(`${path}.mode is invalid`);
    }
    if (rawRule.repeatPolicy !== undefined && !REPEAT_POLICIES.has(rawRule.repeatPolicy)) {
      errors.push(`${path}.repeatPolicy is invalid`);
    }

    const trigger = rawRule.trigger;
    if (!trigger || typeof trigger !== 'object' || Array.isArray(trigger)) {
      errors.push(`${path}.trigger must be an object`);
    } else if (trigger.type !== undefined && !TRIGGER_TYPES.has(trigger.type)) {
      errors.push(`${path}.trigger.type must be "status_entered"`);
    } else {
      const statusCode = normalizeLicenseStatusEmailText(trigger.statusCode);
      if (!statusCode) {
        errors.push(`${path}.trigger.statusCode is required`);
      } else {
        if (configuredStatusCodes.size > 0 && !configuredStatusCodes.has(statusCode)) {
          errors.push(`${path}.trigger.statusCode must match a configured license workflow status`);
        }
        if (seenStatusTriggers.has(statusCode)) {
          errors.push(`${path}.trigger.statusCode must be unique`);
        } else {
          seenStatusTriggers.add(statusCode);
        }
      }
    }

    // Accept old rule-owned designs for migration, but never write them back in normalized rules.
    errors.push(...validatePrintableLicense(rawRule.certificate, { cityId }));
    for (const field of ['attachPrintableLicense', 'sendOnRenewal']) {
      if (rawRule[field] !== undefined && typeof rawRule[field] !== 'boolean') errors.push(`${path}.${field} must be a boolean`);
    }
    const attachmentRule = normalizeLicenseStatusEmailRule(rawRule);
    if (attachmentRule?.attachPrintableLicense && statusConfig.find(status => (status.code || status.id) === rawRule.trigger?.statusCode)?.type !== 'active') {
      errors.push('Printable licenses require an active trigger status.');
    }
    if (rawRule.certificate?.headerLogo) {
      const logoBytes = embeddedEmailImageBytes(rawRule.certificate.headerLogo);
      if (logoBytes === null) errors.push('Printable license header logo must be an embedded PNG no larger than 100 KB.');
      else embeddedBytes += logoBytes;
    }
    if (attachmentRule?.attachPrintableLicense && attachmentRule.sendOnRenewal && rawRule.repeatPolicy !== 'once_per_term') {
      errors.push('Certificates sent on renewal require the once-per-term repeat behavior.');
    }

    const template = rawRule.template;
    if (!template || typeof template !== 'object' || Array.isArray(template)) {
      errors.push(`${path}.template must be an object`);
      return;
    }

    const format = template.format ?? 'standard';
    if (!['standard', 'markdown'].includes(format)) {
      errors.push(`${path}.template.format must be standard or markdown`);
    }

    errors.push(...validateEmbeddedEmailImages(template.images));
    if (template.images && typeof template.images === 'object' && !Array.isArray(template.images)) {
      embeddedBytes += Object.values(template.images)
        .reduce((total, dataUrl) => total + (embeddedEmailImageBytes(dataUrl) || 0), 0);
    }

    const fields = [
      ['subject', LICENSE_STATUS_EMAIL_SUBJECT_MAX_LENGTH],
      ['message', format === 'markdown'
        ? LICENSE_STATUS_EMAIL_MARKDOWN_MAX_LENGTH
        : LICENSE_STATUS_EMAIL_TEMPLATE_TEXT_MAX_LENGTH]
    ];
    fields.forEach(([field, maxLength]) => {
      const text = normalizeLicenseStatusEmailText(template[field]);
      const length = field === 'message' && format === 'markdown' && typeof template[field] === 'string'
        ? template[field].length
        : text?.length;

      if (!text) {
        errors.push(`${path}.template.${field} is required`);
      } else if (length > maxLength) {
        errors.push(`${path}.template.${field} must be ${maxLength} characters or fewer`);
      }
    });
  });

  if (embeddedBytes > EMAIL_TEMPLATE_EMBEDDED_SETTINGS_MAX_BYTES) {
    errors.push('Embedded images must total 300 KB or less across all notification rules.');
  }

  return errors;
}
